import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Upload, Trash2, Send, Check, ExternalLink, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * FlyerStudioCard
 * ---------------
 * Organizer-only card. Two paths:
 *   1. Generate → Nano Banana background + Pillow text overlay (server side).
 *   2. Upload → organizer picks their own PNG/JPG via the shared /uploads
 *      endpoint (image_uploader semantics), then we PUT the returned URL
 *      onto the event doc.
 *
 * After a flyer exists, the organizer can:
 *   • Edit the auto-suggested share caption inline.
 *   • Copy the public landing URL (/flyer/{id}) for social sharing.
 *   • Blast an in-app notification + email to every approved vendor so
 *     they can share it themselves.
 */
export default function FlyerStudioCard({ eventId }) {
  const [flyer, setFlyer] = useState(null);   // { flyer_url, share_caption, ... }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [caption, setCaption] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}`);
      const f = {
        flyer_url: data.flyer_url,
        flyer_source: data.flyer_source,
        share_caption: data.flyer_share_caption || "",
        shared_with_vendors_at: data.flyer_shared_with_vendors_at,
      };
      setFlyer(f);
      setCaption(f.share_caption);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [eventId]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/flyer/generate`);
      setFlyer(data);
      setCaption(data.share_caption || "");
      toast.success("Flyer generated. Preview it below.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
        || "Couldn't generate a flyer — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const uploadOwn = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Guard rails — Nano Banana output is ~350KB but user uploads can be huge.
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Please pick an image under 8MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post(`/uploads`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = up.url || up.file_url;
      if (!url) throw new Error("no url");
      const { data } = await api.put(`/events/${eventId}/flyer`, { flyer_url: url });
      setFlyer(data);
      setCaption(data.share_caption || "");
      toast.success("Flyer uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = ""; // reset input so same file can be picked again
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/events/${eventId}/flyer`);
      setFlyer(null);
      setCaption("");
      toast.success("Flyer removed.");
    } catch (err) {
      toast.error("Couldn't remove — try again");
    } finally {
      setBusy(false);
    }
  };

  const saveCaption = async () => {
    if (!flyer || caption === flyer.share_caption) return;
    try {
      await api.put(`/events/${eventId}/flyer/caption`, { share_caption: caption });
      setFlyer((prev) => ({ ...prev, share_caption: caption }));
      toast.success("Caption saved.");
    } catch (err) {
      toast.error("Couldn't save caption");
    }
  };

  const shareWithVendors = async () => {
    // Ensure the latest caption is persisted before broadcasting.
    if (flyer && caption !== flyer.share_caption) await saveCaption();
    setSharing(true);
    try {
      const { data } = await api.post(`/events/${eventId}/flyer/share-with-vendors`);
      toast.success(`Sent to ${data.notified} approved vendor${data.notified === 1 ? "" : "s"}.`);
      setFlyer((prev) => ({ ...prev, shared_with_vendors_at: new Date().toISOString() }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
        || "Couldn't send — try again");
    } finally {
      setSharing(false);
    }
  };

  const landingPath = `/flyer/${eventId}`;

  return (
    <div className="card-warm rounded-sm p-6" data-testid="flyer-studio-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--moss)" }}>
            <ImageIcon className="h-3 w-3" /> Flyer studio
          </div>
          <div className="font-serif text-2xl mt-1">Ship a flyer in one tap</div>
          <p className="mt-1 text-sm max-w-lg" style={{ color: "var(--ink-soft)" }}>
            Let AI draft a warm, on-brand flyer with your event details baked in,
            or upload your own. Then one-tap share it with every approved vendor.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-sm" style={{ color: "var(--ink-soft)" }}>Loading…</div>
      )}

      {!loading && !flyer?.flyer_url && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="flyer-empty-actions">
          <Button
            type="button"
            data-testid="flyer-generate-btn"
            onClick={generate}
            disabled={busy}
            className="btn-primary rounded-full"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            {busy ? "Generating…" : "Generate with AI"}
          </Button>
          <label
            className="btn-outline rounded-full inline-flex items-center gap-1.5 px-4 py-2 text-sm cursor-pointer"
            data-testid="flyer-upload-label"
          >
            <Upload className="h-4 w-4" /> Upload my own
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={uploadOwn}
              disabled={busy}
              className="hidden"
              data-testid="flyer-upload-input"
            />
          </label>
        </div>
      )}

      {!loading && flyer?.flyer_url && (
        <div className="mt-5" data-testid="flyer-preview">
          <div
            className="rounded-sm overflow-hidden border max-w-md mx-auto"
            style={{ borderColor: "var(--sand)" }}
          >
            <img
              src={flyer.flyer_url}
              alt="Event flyer"
              data-testid="flyer-preview-image"
              className="w-full h-auto block"
            />
          </div>

          <div className="mt-4">
            <Label className="text-xs uppercase tracking-widest">Share caption</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onBlur={saveCaption}
              rows={3}
              maxLength={500}
              data-testid="flyer-caption-input"
              placeholder="Come find us at…"
              className="mt-1"
            />
            <div className="text-[11px] text-right" style={{ color: "var(--ink-soft)" }}>
              {caption.length}/500
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              data-testid="flyer-share-with-vendors-btn"
              onClick={shareWithVendors}
              disabled={sharing}
              className="btn-primary rounded-full"
            >
              <Send className="h-4 w-4 mr-1.5" />
              {sharing ? "Sending…" : "Share with approved vendors"}
            </Button>
            <Link
              to={landingPath}
              data-testid="flyer-view-landing-link"
              target="_blank"
              rel="noreferrer"
              className="btn-outline rounded-full inline-flex items-center gap-1.5 px-4 py-2 text-sm"
            >
              <ExternalLink className="h-4 w-4" /> View share page
            </Link>
            <Button
              type="button"
              data-testid="flyer-regenerate-btn"
              onClick={generate}
              disabled={busy}
              variant="outline"
              className="rounded-full"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {busy ? "Generating…" : "Regenerate"}
            </Button>
            <Button
              type="button"
              data-testid="flyer-remove-btn"
              onClick={remove}
              disabled={busy}
              variant="ghost"
              className="rounded-full text-xs"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
            </Button>
          </div>

          {flyer.shared_with_vendors_at && (
            <p className="mt-3 text-xs inline-flex items-center gap-1.5"
               style={{ color: "var(--moss)" }}
               data-testid="flyer-shared-indicator">
              <Check className="h-3.5 w-3.5" /> Sent to your approved vendors.
              Regenerating & sending again is fine — they&apos;ll get the latest.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
